import type { SignalMetadataSchema } from "@repo/app-types/entities/signal";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { SimpleMenu } from "@repo/ui/Menu/simpleMenu";
import { SimpleMenuItem } from "@repo/ui/Menu/simpleMenuItem";
import { SimpleMenuPopover } from "@repo/ui/Menu/simpleMenuPopover";
import { IconArrowLeft, IconChevronDown } from "@tabler/icons-react";
import type { FC } from "react";
import { DialogTrigger } from "react-aria-components";
import { MetadataSchemaBuilder } from "./metadataSchemaBuilder.js";
import {
  SignalTestResultPill,
  type SignalTestResultPillState,
} from "./signalTestResultPill.js";

export interface SignalEditorContainerProps {
  name: string;
  description: string;
  metadataSchema: SignalMetadataSchema;
  error: string | null;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onMetadataSchemaChange: (value: SignalMetadataSchema) => void;
  onClose: () => void;
  onDiscard?: () => void;
  onSubmit: () => void;
  isLoading: boolean;
  isSubmitDisabled: boolean;
  submitLabel: string;
  showDiscard: boolean;
  onTestSignalClick: () => void;
  onConfigureTestClick: () => void;
  isTestSignalDisabled: boolean;
  testResultPillState: SignalTestResultPillState | null;
  onViewMatches: () => void;
}

export const SignalEditorContainer: FC<SignalEditorContainerProps> = ({
  name,
  description,
  metadataSchema,
  error,
  onNameChange,
  onDescriptionChange,
  onMetadataSchemaChange,
  onClose,
  onDiscard,
  onSubmit,
  isLoading,
  isSubmitDisabled,
  submitLabel,
  showDiscard,
  onTestSignalClick,
  onConfigureTestClick,
  isTestSignalDisabled,
  testResultPillState,
  onViewMatches,
}) => {
  return (
    <div className="flex h-full flex-col">
      {/* Top toolbar */}
      <div className="flex items-center justify-between border-b border-border-light bg-white px-4 py-2">
        <Button
          variant={ButtonVariant.Tertiary}
          onClick={onClose}
          icon={<IconArrowLeft className="h-4 w-4" />}
          size={Size.MEDIUM}
        >
          Back
        </Button>
        <div className="flex items-center gap-2">
          <div className="flex items-stretch">
            <Button
              variant={ButtonVariant.Secondary}
              onClick={onTestSignalClick}
              isDisabled={isTestSignalDisabled}
              size={Size.MEDIUM}
              className="rounded-r-none"
            >
              Test signal
            </Button>
            <DialogTrigger>
              <Button
                variant={ButtonVariant.Secondary}
                icon={<IconChevronDown className="h-4 w-4" />}
                isDisabled={isTestSignalDisabled}
                size={Size.MEDIUM}
                className="-ml-px rounded-l-none px-2"
              />
              <SimpleMenuPopover placement="bottom end">
                <SimpleMenu aria-label="Test signal options">
                  <SimpleMenuItem onAction={onConfigureTestClick}>
                    Configure test…
                  </SimpleMenuItem>
                </SimpleMenu>
              </SimpleMenuPopover>
            </DialogTrigger>
          </div>
          {showDiscard && onDiscard && (
            <Button
              variant={ButtonVariant.Secondary}
              onClick={onDiscard}
              size={Size.MEDIUM}
            >
              Discard
            </Button>
          )}
          <Button
            variant={ButtonVariant.Primary}
            onClick={onSubmit}
            isDisabled={isSubmitDisabled}
            isLoading={isLoading}
            size={Size.MEDIUM}
          >
            {submitLabel}
          </Button>
        </div>
      </div>

      {/* Form content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="mx-auto flex max-w-xl flex-col space-y-6">
          {testResultPillState ? (
            <SignalTestResultPill
              state={testResultPillState}
              onViewMatches={onViewMatches}
            />
          ) : null}

          <div>
            <Input
              label="Name"
              id="signal_name"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="Signal name"
              maxLength={200}
            />
          </div>

          <div>
            <Textarea
              label="Description"
              id="signal_description"
              value={description}
              onChange={onDescriptionChange}
              placeholder="e.g. Customer mentioning one of our competitors"
              description="A natural language definition of the conversational pattern to detect."
              maxLength={5000}
              height="15rem"
            />
          </div>

          <div>
            <MetadataSchemaBuilder
              value={metadataSchema}
              onChange={onMetadataSchemaChange}
            />
          </div>

          {error && <div className="text-sm text-destructive">{error}</div>}
        </div>
      </div>
    </div>
  );
};
